<template>
    <div>

        <!-- MODE -->
        <p>
            Mode
            <b-select v-model="mode" :options="[
                { text: 'Fichiers', value: 'fichiers' },
                { text: 'CSV',      value: 'csv'      }
            ]" />
        </p>

        <!-- ACTION -->
        <p>
            Action
            <b-select v-model="action" :options="action_options" />
        </p>

        <!-- ─── ACTION GET ─────────────────────────────────────── -->
        <div v-if="action === 'get'">
            <p>
                Colonne des UIDs
                <b-input :list="'uid_get_' + id" v-model="column_uid"
                    placeholder="ex : uid" />
                <datalist :id="'uid_get_' + id">
                    <option v-for="c in columns_data" :key="c">{{ c }}</option>
                </datalist>
            </p>
            <p>
                Préfixe des colonnes résultat
                <small class="text-muted">(ex&nbsp;: <code>doc_</code> → <code>doc_libelle</code>, <code>doc_public</code>…)</small>
                <b-input v-model="get_prefix" placeholder="doc_" />
            </p>
            <p>
                Colonnes à récupérer
                <small class="text-muted">(séparées par virgule — laisser vide pour toutes)</small>
                <b-input v-model="get_columns" placeholder="libelle,public,tags,dateheure" />
            </p>
        </div>

        <!-- ─── ACTION SUPPRIMER (csv) : uniquement colonne UID ── -->
        <div v-else-if="action === 'Supprimer'">
            <p>
                Colonne des UIDs
                <b-input :list="'col_uid_del_' + id" v-model="column_uid" placeholder="uid" />
                <datalist :id="'col_uid_del_' + id">
                    <option v-for="c in columns_data" :key="c">{{ c }}</option>
                </datalist>
            </p>
        </div>

        <!-- ─── ACTION AJOUTER / METTRE À JOUR ───────────────── -->
        <div v-else>

            <!-- CSV-specific -->
            <div v-if="mode === 'csv'">
                <p>
                    Colonne des URLs
                    <b-input :list="'col_url_' + id" v-model="url" placeholder="url" />
                    <datalist :id="'col_url_' + id">
                        <option v-for="c in columns_data" :key="c">{{ c }}</option>
                    </datalist>
                </p>
                <p>
                    Colonne des noms
                    <b-input :list="'col_name_' + id" v-model="name" placeholder="nom" />
                    <datalist :id="'col_name_' + id">
                        <option v-for="c in columns_data" :key="c">{{ c }}</option>
                    </datalist>
                </p>
                <p>
                    Colonne des UIDs
                    <b-input :list="'col_uid_' + id" v-model="column_uid" placeholder="uid" />
                    <datalist :id="'col_uid_' + id">
                        <option v-for="c in columns_data" :key="c">{{ c }}</option>
                    </datalist>
                </p>
            </div>

            <!-- Dossier (csv + fichiers) -->
            <p>
                Dossier
                <small class="text-muted">
                    — colonne CSV, valeur brute ou template lodash
                    (<code>&lt;%= data.categorie %&gt;</code>)
                </small>
                <b-input :list="'col_folder_' + id" v-model="folder"
                    placeholder="ex : archives/<%= data.annee %>" />
                <datalist :id="'col_folder_' + id">
                    <option v-for="c in columns_data" :key="c">{{ c }}</option>
                </datalist>
            </p>

            <!-- Public -->
            <p>
                <b-checkbox v-model="is_public">Public</b-checkbox>
            </p>

            <!-- Return files -->
            <p>
                <b-checkbox v-model="return_files">Renvoyer les fichiers</b-checkbox>
            </p>

            <!-- No thumbnail -->
            <p>
                <b-checkbox v-model="no_thumbnail">Ne pas générer de miniature</b-checkbox>
            </p>

            <!-- ─── Tags et colonnes GED ─────────────────────── -->
            <!-- ─── Tags et colonnes GED ─────────────────────── -->
            <div>
                <p class="mb-1">
                    <strong>Tags et colonnes GED</strong>
                    <small class="text-muted ml-2">
                        source « <em>colonne CSV</em> » → lire la valeur depuis cette colonne ;
                        « <em>valeur brute</em> » → texte fixe ;
                        « <em>template</em> » → <code>&lt;%= data.col %&gt;</code>
                    </small>
                </p>

                <!-- Colonnes disponibles depuis la config client -->
                <div v-if="ged_columns_list.length > 0"
                    class="mb-3 p-2 border rounded"
                    style="background:#f8f9fa; font-size:.86rem">
                    <div class="text-muted mb-1">
                        <strong>Colonnes disponibles</strong> — cliquer pour ajouter au mapping :
                    </div>
                    <div class="d-flex flex-wrap" style="gap:.35rem">
                        <span
                            v-for="col in ged_columns_list" :key="col.column"
                            class="badge px-2 py-1"
                            :class="col.type === 'json' ? 'badge-info' : 'badge-secondary'"
                            style="cursor:pointer; font-weight:normal; font-size:.82rem"
                            v-b-tooltip.hover
                            :title="(col.label && col.label !== col.column ? col.label + ' — ' : '') + (col.type === 'json' ? 'Tableau JSON (tags)' : 'Texte')"
                            @click="add_ged_column(col.column)">
                            {{ col.column }}<span v-if="col.label && col.label !== col.column" class="ml-1 font-italic" style="opacity:.75;font-size:.78em">{{ col.label }}</span>
                            <i :class="col.type === 'json' ? 'fas fa-list ml-1' : 'fas fa-font ml-1'"
                                style="font-size:.72em" />
                        </span>
                    </div>
                </div>
                <div v-else-if="client" class="text-muted mb-2" style="font-size:.85rem">
                    <i class="fas fa-info-circle mr-1" />Aucune colonne GED configurée pour ce client.
                </div>

                <b-table-simple small borderless class="mb-2">
                    <b-thead>
                        <b-tr>
                            <b-th style="width:200px">
                                Colonne GED
                                <small class="d-block font-weight-normal text-muted" style="font-size:.78rem">
                                    <b-badge variant="info" style="font-size:.7rem">[ ]</b-badge> tableau JSON &nbsp;
                                    <b-badge variant="secondary" style="font-size:.7rem">T</b-badge> texte
                                </small>
                            </b-th>
                            <b-th style="width:140px">Source</b-th>
                            <b-th>Valeur / colonne CSV / template</b-th>
                            <b-th style="width:40px"></b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(row, idx) in columns_config" :key="idx">
                            <b-td>
                                <div class="d-flex align-items-center" style="gap:.25rem">
                                    <b-badge v-if="ged_col_type(row.column) === 'json'" variant="info"
                                        v-b-tooltip.hover title="Tableau JSON (tags)"
                                        class="flex-shrink-0">[ ]</b-badge>
                                    <b-badge v-else-if="ged_col_type(row.column) === 'text'" variant="secondary"
                                        v-b-tooltip.hover title="Texte"
                                        class="flex-shrink-0">T</b-badge>
                                    <b-badge v-else-if="row.column" variant="light"
                                        class="flex-shrink-0"
                                        style="border:1px solid #ccc">?</b-badge>
                                    <b-input v-model="row.column" size="sm"
                                        :list="'cfg_col_' + id + '_' + idx"
                                        placeholder="ex : categorie" />
                                    <datalist :id="'cfg_col_' + id + '_' + idx">
                                        <option v-for="col in ged_columns_list" :key="col.column">{{ col.column }}</option>
                                    </datalist>
                                </div>
                            </b-td>
                            <b-td>
                                <b-select v-model="row.source" size="sm" :options="[
                                    { text: 'Colonne CSV',  value: 'csv'      },
                                    { text: 'Valeur brute', value: 'value'    },
                                    { text: 'Template',     value: 'template' }
                                ]" />
                            </b-td>
                            <b-td>
                                <b-input v-model="row.value" size="sm"
                                    :list="'cfg_val_' + id + '_' + idx"
                                    :placeholder="row.source === 'csv'
                                        ? 'nom_colonne_csv'
                                        : row.source === 'template'
                                            ? '<%= data.col %>'
                                            : 'valeur fixe'" />
                                <datalist :id="'cfg_val_' + id + '_' + idx">
                                    <option v-for="c in columns_data" :key="c">{{ c }}</option>
                                </datalist>
                            </b-td>
                            <b-td>
                                <b-button size="sm" variant="outline-danger"
                                    @click="columns_config.splice(idx, 1)">
                                    <i class="fas fa-times" />
                                </b-button>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <b-button size="sm" variant="outline-success"
                    @click="columns_config.push({ column: '', source: 'csv', value: '' })">
                    <i class="fas fa-plus mr-1" />Ajouter une colonne
                </b-button>
            </div>

        </div>

    </div>
</template>

<script>
    import uniqueId from "lodash.uniqueid";

    export default {
        name: "share_document_v2",

        props: {
            treatment_params: { default: false },
            files:            { default: () => [] },
            data:             { default: () => [] },
            client:           { default: null },
            size:             { default: 1000 },
            step_position:    { default: 0 }
        },

        computed: {
            action_options() {
                if (this.mode === 'fichiers') {
                    return [
                        { text: 'Ajouter ou mettre à jour', value: 'Ajouter ou mettre à jour' }
                    ];
                }
                return [
                    { text: 'Ajouter ou mettre à jour', value: 'Ajouter ou mettre à jour' },
                    { text: 'Supprimer',                value: 'Supprimer'                },
                    { text: 'Récupérer (get)',           value: 'get'                     }
                ];
            },
            ged_columns_list() {
                if (!this.client || !this.client.details) return [];
                var result = [];
                var seen = {};

                // Source 1 : details.columns_ged {colonne: {type:'tags'|'column', label, ...}}
                var columns_ged = this.client.details.columns_ged;
                if (columns_ged && typeof columns_ged === 'object' && !Array.isArray(columns_ged)) {
                    Object.keys(columns_ged).forEach(function(col) {
                        var def = columns_ged[col];
                        seen[col] = true;
                        result.push({
                            column: col,
                            type: def.type === 'tags' ? 'json' : 'text',
                            label: def.label || col
                        });
                    });
                }

                // Source 2 : details.tags objet {colonne: [...valeurs]} (format DocumentDb)
                // ou chaîne simple "nom_colonne" → une seule colonne tag
                // On ignore si c'est un tableau (tags projet)
                var tags = this.client.details.tags;
                if (tags && typeof tags === 'string') {
                    if (!seen[tags]) {
                        seen[tags] = true;
                        result.push({ column: tags, type: 'json', label: 'Tags' });
                    }
                } else if (tags && typeof tags === 'object' && !Array.isArray(tags)) {
                    Object.keys(tags).forEach(function(col) {
                        if (!seen[col]) {
                            seen[col] = true;
                            result.push({ column: col, type: 'json', label: col });
                        }
                    });
                }

                // Source 3 : details.document.columns_other (colonnes texte)
                var columns_other = (this.client.details.document && this.client.details.document.columns_other)
                    ? this.client.details.document.columns_other : [];
                columns_other.forEach(function(col) {
                    if (!seen[col]) {
                        seen[col] = true;
                        result.push({ column: col, type: 'text', label: col });
                    }
                });

                return result;
            }
        },

        watch: {
            mode(val) {
                if (val === 'fichiers' && this.action !== 'Ajouter ou mettre à jour') {
                    this.action = 'Ajouter ou mettre à jour';
                }
            },
            data: {
                deep: true,
                immediate: true,
                handler() {
                    if (this.data && this.data.length > 0)
                        this.columns_data = Object.keys(this.data[0]);
                }
            },
            files: {
                deep: true,
                immediate: true,
                handler() {
                    // En mode fichiers, les fichiers sont des documents (PDF, etc.) — ne pas parser
                    if (this.mode !== 'csv') return;
                    if (this.files && this.files.length > 0) {
                        var self = this;
                        var reader = new FileReader();
                        reader.onload = (function(theFile) {
                            return function(e) {
                                if (e.target.result) {
                                    var parsed = self.$papa.parse(e.target.result, { header: true });
                                    if (parsed.data && parsed.data.length > 0)
                                        self.columns_data = Object.keys(parsed.data[0]);
                                }
                            };
                        })(this.files[0].file ? this.files[0].file : this.files[0]);
                        reader.readAsText(this.files[0].file ? this.files[0].file : this.files[0]);
                    }
                }
            }
        },

        data() {
            return {
                id: uniqueId(""),

                // champs communs
                mode:        "fichiers",
                action:      "Ajouter ou mettre à jour",

                // csv add/update
                url:         "",
                name:        "",
                column_uid:  "",

                // dossier + options
                folder:       "",
                is_public:    false,
                return_files: false,
                no_thumbnail: false,

                // colonnes GED / tags  →  [{column, source, value}]
                columns_config: [],

                // action get
                get_prefix:  "doc_",
                get_columns: "",

                // colonnes CSV disponibles
                columns_data: []
            };
        },

        methods: {
            get_data() {
                return this.save();
            },

            ged_col_type(col) {
                var found = this.ged_columns_list.find(function(c) { return c.column === col; });
                return found ? found.type : null;
            },

            add_ged_column(col) {
                if (!this.columns_config.find(function(r) { return r.column === col; })) {
                    this.columns_config.push({ column: col, source: 'csv', value: '' });
                }
            },

            save() {
                return {
                    mode:           this.mode,
                    action:         this.action,
                    url:            this.url,
                    name:           this.name,
                    column_uid:     this.column_uid,
                    folder:         this.folder,
                    public:         this.is_public,
                    return_files:   this.return_files,
                    no_thumbnail:   this.no_thumbnail,
                    columns_config: this.columns_config,
                    get_prefix:     this.get_prefix,
                    get_columns:    this.get_columns
                };
            },

            load(data) {
                if (!data) return;
                this.mode           = data.mode          || "fichiers";
                this.action         = data.action        || "Ajouter ou mettre à jour";
                this.url            = data.url           || "";
                this.name           = data.name          || "";
                this.column_uid     = data.column_uid    || "";
                this.folder         = data.folder        || "";
                this.is_public      = data.public        || false;
                this.return_files   = data.return_files  || false;
                this.no_thumbnail   = data.no_thumbnail  || false;
                this.columns_config = data.columns_config ? JSON.parse(JSON.stringify(data.columns_config)) : [];
                this.get_prefix     = data.get_prefix !== undefined ? data.get_prefix : "doc_";
                this.get_columns    = data.get_columns   || "";
            }
        },

        mounted() {}
    }
</script>

<style scoped>
</style>
